import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "tutorial-center" }
const _hoisted_2 = { class: "tutorial-sidebar" }
const _hoisted_3 = ["onClick"]
const _hoisted_4 = ["src"]
const _hoisted_5 = { class: "tutorial-content" }
const _hoisted_6 = { class: "tutorial-tabs" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { key: 0 }
const _hoisted_9 = { class: "tutorial-scroll" }
const _hoisted_10 = {
  key: 0,
  class: "tutorial-state tutorial-loading"
}
const _hoisted_11 = {
  key: 1,
  class: "tutorial-state"
}
const _hoisted_12 = {
  key: 2,
  class: "tutorial-state"
}
const _hoisted_13 = {
  key: 3,
  class: "tutorial-grid"
}
const _hoisted_14 = ["onClick"]
const _hoisted_15 = { class: "card-preview" }
const _hoisted_16 = ["src", "alt", "onError"]
const _hoisted_17 = { class: "card-footer" }
const _hoisted_18 = { class: "card-title" }
const _hoisted_19 = {
  key: 0,
  class: "tutorial-video-container"
}
const _hoisted_20 = {
  key: 0,
  class: "bilibili-player-container"
}
const _hoisted_21 = ["src"]
const _hoisted_22 = {
  key: 1,
  class: "video-player-state"
}
const _hoisted_23 = {
  key: 2,
  class: "video-player-state"
}

import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { $config, $enum } from '@/global'
import {
  getTutorialCenterList,
  TutorialCard,
  TutorialPrimaryCategory,
  TutorialContentType,
  TutorialVideoSource,
} from '@/service'
import categoryCaacIcon from '@/assets/images/tutorial-category-caac@2x.png'
import categoryPowerIcon from '@/assets/images/tutorial-category-power@2x.png'
import categoryQuestionIcon from '@/assets/images/tutorial-category-question@2x.png'
import filePdfIcon from '@/assets/images/tutorial-file-pdf@2x.png'
import fileVideoIcon from '@/assets/images/tutorial-file-video@2x.png'


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const categoryIcons: Record<string, string> = {
  CAAC_MULTI_ROTOR: categoryCaacIcon,
  POWER_INSPECTION: categoryPowerIcon,
  FAQ: categoryQuestionIcon,
}

const fileIcons: Record<TutorialContentType, string> = {
  PDF: filePdfIcon,
  VIDEO: fileVideoIcon,
}

const primaryCategories = ref<TutorialPrimaryCategory[]>([])
const activePrimaryCategoryId = ref('')
const activeContentCategoryId = ref('')
const loading = ref(false)
const errorMessage = ref('')
const showVideoPlayer = ref(false)
const videoResource = ref('')
const activeVideoSource = ref<TutorialVideoSource | null>(null)
const originalVideoUrl = ref('')
const bilibiliPlayerUrl = ref('')
const bilibiliPlayerKey = ref(0)
const bilibiliLoading = ref(false)
const bilibiliLoadFailed = ref(false)
const failedCoverIds = ref<Record<string, boolean>>({})
let bilibiliLoadTimer: number | undefined

const activePrimaryCategory = computed(() => {
  return primaryCategories.value.find(item => item.id === activePrimaryCategoryId.value)
})

const contentCategories = computed(() => activePrimaryCategory.value?.contentCategories || [])

const tutorials = computed(() => {
  return contentCategories.value.find(item => item.id === activeContentCategoryId.value)?.tutorials || []
})

function selectCategory(category?: TutorialPrimaryCategory) {
  activePrimaryCategoryId.value = category?.id || ''
  activeContentCategoryId.value = category?.contentCategories?.[0]?.id || ''
}

function handleCategoryChange(category: TutorialPrimaryCategory) {
  if (category.id === activePrimaryCategoryId.value) return
  selectCategory(category)
}

function getCategoryIcon(code: string) {
  return categoryIcons[code] || categoryQuestionIcon
}

function getContentTypeLabel(contentType: TutorialContentType) {
  return contentType === 'VIDEO' ? '视频' : 'PDF'
}

function shouldShowVideoCover(tutorial: TutorialCard) {
  return tutorial.contentType === 'VIDEO'
    && Boolean(tutorial.coverUrl)
    && !failedCoverIds.value[tutorial.id]
}

function getCardPreviewUrl(tutorial: TutorialCard) {
  if (shouldShowVideoCover(tutorial)) {
    return resolveResourceUrl(tutorial.coverUrl as string)
  }
  return fileIcons[tutorial.contentType]
}

function handleCoverLoadFailed(tutorial: TutorialCard) {
  if (!shouldShowVideoCover(tutorial)) return
  failedCoverIds.value = {
    ...failedCoverIds.value,
    [tutorial.id]: true,
  }
}

function resolveResourceUrl(contentUrl: string) {
  if (/^https?:\/\//i.test(contentUrl)) return contentUrl
  return `${$config.FILE_ROOT_PATH || ''}${contentUrl}`
}

function getBilibiliPlayerUrl(contentUrl: string) {
  const sourceUrl = contentUrl.trim()
  let bvid = ''
  let page = '1'

  try {
    const url = new URL(sourceUrl, window.location.origin)
    bvid = url.searchParams.get('bvid') || url.pathname.match(/\/video\/(BV[a-zA-Z0-9]+)/i)?.[1] || ''
    const pageParam = url.searchParams.get('p')
    if (pageParam && /^\d+$/.test(pageParam)) page = pageParam
  } catch (error) {
    bvid = sourceUrl.match(/(BV[a-zA-Z0-9]+)/i)?.[1] || ''
  }

  if (!/^BV[a-zA-Z0-9]+$/i.test(bvid)) return ''

  const params = new URLSearchParams({
    bvid,
    p: page,
    autoplay: '0',
    danmaku: '0',
  })
  return `https://player.bilibili.com/player.html?${params.toString()}`
}

function clearBilibiliLoadTimer() {
  if (bilibiliLoadTimer !== undefined) {
    window.clearTimeout(bilibiliLoadTimer)
    bilibiliLoadTimer = undefined
  }
}

function startBilibiliLoading() {
  clearBilibiliLoadTimer()
  bilibiliLoading.value = true
  bilibiliLoadFailed.value = false
  bilibiliLoadTimer = window.setTimeout(handleBilibiliLoadFailed, 15000)
}

function handleOpenTutorial(tutorial: TutorialCard) {
  if (!tutorial.contentUrl) return

  const resourceUrl = resolveResourceUrl(tutorial.contentUrl)
  if (tutorial.contentType === 'VIDEO') {
    activeVideoSource.value = tutorial.videoSource
    originalVideoUrl.value = resourceUrl
    showVideoPlayer.value = true

    if (tutorial.videoSource === 'BILIBILI') {
      bilibiliPlayerUrl.value = getBilibiliPlayerUrl(tutorial.contentUrl)
      if (bilibiliPlayerUrl.value) {
        bilibiliPlayerKey.value += 1
        startBilibiliLoading()
      } else {
        bilibiliLoading.value = false
        bilibiliLoadFailed.value = true
      }
      return
    }

    videoResource.value = resourceUrl
    return
  }

  window.open(resourceUrl, '_blank', 'noopener')
}

function handleBilibiliLoaded() {
  clearBilibiliLoadTimer()
  bilibiliLoading.value = false
  bilibiliLoadFailed.value = false
}

function handleBilibiliLoadFailed() {
  clearBilibiliLoadTimer()
  bilibiliLoading.value = false
  bilibiliLoadFailed.value = true
}

function retryBilibiliPlayer() {
  if (!bilibiliPlayerUrl.value) {
    bilibiliPlayerUrl.value = getBilibiliPlayerUrl(originalVideoUrl.value)
  }
  if (!bilibiliPlayerUrl.value) return

  bilibiliPlayerKey.value += 1
  startBilibiliLoading()
}

function openOriginalVideo() {
  if (!originalVideoUrl.value) return
  window.open(originalVideoUrl.value, '_blank', 'noopener')
}

function closeVideoPlayer() {
  clearBilibiliLoadTimer()
  showVideoPlayer.value = false
  videoResource.value = ''
  activeVideoSource.value = null
  originalVideoUrl.value = ''
  bilibiliPlayerUrl.value = ''
  bilibiliLoading.value = false
  bilibiliLoadFailed.value = false
}

async function loadTutorialCenter() {
  loading.value = true
  errorMessage.value = ''

  try {
    const { code, data } = await getTutorialCenterList()
    if (code != $enum.EnumResult.SUCCESS) {
      throw new Error('TUTORIAL_CENTER_REQUEST_FAILED')
    }

    failedCoverIds.value = {}
    primaryCategories.value = Array.isArray(data?.primaryCategories) ? data.primaryCategories : []
    const defaultCategory = primaryCategories.value.find(item => item.code === 'CAAC_MULTI_ROTOR')
      || primaryCategories.value[0]
    selectCategory(defaultCategory)
  } catch (error) {
    primaryCategories.value = []
    selectCategory()
    const status = (error as { response?: { status?: number } })?.response?.status
    errorMessage.value = status === 503
      ? '教程服务暂不可用，请稍后重试'
      : '教程数据加载失败，请稍后重试'
  } finally {
    loading.value = false
  }
}

onMounted(loadTutorialCenter)
onBeforeUnmount(clearBilibiliLoadTimer)

return (_ctx: any,_cache: any) => {
  const _component_vkm_videoPlayer = _resolveComponent("vkm-videoPlayer")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("aside", _hoisted_2, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(primaryCategories.value, (category) => {
        return (_openBlock(), _createElementBlock("button", {
          key: category.id,
          type: "button",
          class: _normalizeClass(["category-item", { active: activePrimaryCategoryId.value === category.id }]),
          onClick: ($event: any) => (handleCategoryChange(category))
        }, [
          _createElementVNode("img", {
            src: getCategoryIcon(category.code),
            alt: ""
          }, null, 8, _hoisted_4),
          _createElementVNode("span", null, _toDisplayString(category.name), 1)
        ], 10, _hoisted_3))
      }), 128))
    ]),
    _createElementVNode("main", _hoisted_5, [
      _createElementVNode("nav", _hoisted_6, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(contentCategories.value, (tab) => {
          return (_openBlock(), _createElementBlock("button", {
            key: tab.id,
            type: "button",
            class: _normalizeClass(["tutorial-tab", { active: activeContentCategoryId.value === tab.id }]),
            onClick: ($event: any) => (activeContentCategoryId.value = tab.id)
          }, [
            _createElementVNode("span", null, _toDisplayString(tab.name), 1),
            (activeContentCategoryId.value === tab.id)
              ? (_openBlock(), _createElementBlock("i", _hoisted_8))
              : _createCommentVNode("", true)
          ], 10, _hoisted_7))
        }), 128))
      ]),
      _createElementVNode("div", _hoisted_9, [
        (loading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, _cache[0] || (_cache[0] = [
              _createElementVNode("i", { class: "loading-spinner" }, null, -1),
              _createElementVNode("span", null, "加载中...", -1)
            ])))
          : (errorMessage.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                _createElementVNode("span", null, _toDisplayString(errorMessage.value), 1),
                _createElementVNode("button", {
                  type: "button",
                  class: "retry-button",
                  onClick: loadTutorialCenter
                }, "重新加载")
              ]))
            : (tutorials.value.length === 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_12, " 暂无教程内容 "))
              : (_openBlock(), _createElementBlock("div", _hoisted_13, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tutorials.value, (tutorial) => {
                    return (_openBlock(), _createElementBlock("button", {
                      key: tutorial.id,
                      type: "button",
                      class: "tutorial-card",
                      onClick: ($event: any) => (handleOpenTutorial(tutorial))
                    }, [
                      _createElementVNode("div", _hoisted_15, [
                        _createElementVNode("img", {
                          src: getCardPreviewUrl(tutorial),
                          class: _normalizeClass({ 'video-cover': shouldShowVideoCover(tutorial) }),
                          alt: getContentTypeLabel(tutorial.contentType),
                          onError: ($event: any) => (handleCoverLoadFailed(tutorial))
                        }, null, 42, _hoisted_16)
                      ]),
                      _createElementVNode("div", _hoisted_17, [
                        _createElementVNode("span", _hoisted_18, _toDisplayString(tutorial.name), 1),
                        _createElementVNode("span", {
                          class: _normalizeClass(["file-tag", `tag-${tutorial.contentType.toLowerCase()}`])
                        }, _toDisplayString(getContentTypeLabel(tutorial.contentType)), 3)
                      ])
                    ], 8, _hoisted_14))
                  }), 128))
                ]))
      ])
    ]),
    (showVideoPlayer.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
          (activeVideoSource.value === 'BILIBILI')
            ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                _createElementVNode("button", {
                  type: "button",
                  class: "video-close-button",
                  "aria-label": "关闭视频",
                  onClick: closeVideoPlayer
                }, _cache[1] || (_cache[1] = [
                  _createElementVNode("i", { class: "iconfont-line vk_gb_line1" }, null, -1)
                ])),
                (bilibiliPlayerUrl.value)
                  ? (_openBlock(), _createElementBlock("iframe", {
                      key: bilibiliPlayerKey.value,
                      class: "bilibili-player",
                      src: bilibiliPlayerUrl.value,
                      title: "哔哩哔哩视频播放器",
                      scrolling: "no",
                      frameborder: "0",
                      allow: "autoplay; fullscreen; picture-in-picture; encrypted-media",
                      allowfullscreen: "",
                      referrerpolicy: "strict-origin-when-cross-origin",
                      onLoad: handleBilibiliLoaded,
                      onError: handleBilibiliLoadFailed
                    }, null, 40, _hoisted_21))
                  : _createCommentVNode("", true),
                (bilibiliLoading.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_22, _cache[2] || (_cache[2] = [
                      _createElementVNode("i", { class: "loading-spinner" }, null, -1),
                      _createElementVNode("span", null, "视频加载中...", -1)
                    ])))
                  : (bilibiliLoadFailed.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                        _cache[3] || (_cache[3] = _createElementVNode("span", null, "视频加载失败，请重试", -1)),
                        _createElementVNode("div", { class: "video-state-actions" }, [
                          _createElementVNode("button", {
                            type: "button",
                            class: "retry-button",
                            onClick: retryBilibiliPlayer
                          }, "重新加载"),
                          _createElementVNode("button", {
                            type: "button",
                            class: "retry-button",
                            onClick: openOriginalVideo
                          }, "前往哔哩哔哩")
                        ])
                      ]))
                    : _createCommentVNode("", true)
              ]))
            : (_openBlock(), _createBlock(_component_vkm_videoPlayer, {
                key: 1,
                onOnClose: closeVideoPlayer,
                videoSrc: videoResource.value
              }, null, 8, ["videoSrc"]))
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})